import { useNavigate, useParams } from "react-router-dom";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { LoadingState } from "@/components/profile/LoadingState";
import { ErrorState } from "@/components/profile/ErrorState";
import { ProfileNav } from "@/components/profile/ProfileNav";
import { ListingsSection } from "@/components/profile/ListingsSection";
import { ContactWidget } from "@/components/profile/ContactWidget";
import { useProfileData } from "@/hooks/use-profile-data";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { parseFAQs, Profile } from "@/components/profile/types/contact-widget";
import type { Json } from "@/integrations/supabase/types";

interface MySpaceProps {
  isPublic?: boolean;
}

const getVisitorIdentifier = async (): Promise<string> => {
  try {
    const ipServices = [
      'https://api.ipify.org?format=json',
      'https://ip.seeip.org/jsonip',
      'https://api.ip.sb/jsonip',
      'https://api64.ipify.org?format=json'
    ];

    for (const service of ipServices) {
      try {
        const response = await fetch(service);
        const data = await response.json();
        if (data.ip) return data.ip;
      } catch (error) {
        console.error(`Error with ${service}:`, error);
        continue;
      }
    }

    try {
      const response = await fetch('https://1.1.1.1/cdn-cgi/trace');
      const data = await response.text();
      const ip = data.split('\n').find(line => line.startsWith('ip='))?.split('=')[1];
      if (ip) return ip;
    } catch (error) {
      console.error('Error with Cloudflare trace:', error);
    }

    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      new Date().getTimezoneOffset(),
      screen.width,
      screen.height,
      navigator.hardwareConcurrency,
      await getCanvasFingerprint(),
      await getAudioFingerprint(),
      await getWebGLFingerprint(),
      await getFontFingerprint(),
      await getHardwareInfo()
    ].join('|');
    
    return Array.from(
      new Uint8Array(
        await crypto.subtle.digest('SHA-256', new TextEncoder().encode(fingerprint))
      )
    ).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    console.error('Error getting visitor identifier:', error);
    return `${Date.now()}-${Math.random().toString(36).substring(2)}`;
  }
};

const getCanvasFingerprint = async (): Promise<string> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  canvas.width = 200;
  canvas.height = 200;

  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillStyle = '#f60';
  ctx.fillRect(125,1,62,20);
  ctx.fillStyle = '#069';
  ctx.fillText('Browser Fingerprint',2,15);
  ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
  ctx.fillText('Canvas Test',4,45);
  
  return canvas.toDataURL();
};

const getAudioFingerprint = async (): Promise<string> => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const analyser = audioContext.createAnalyser();
    oscillator.connect(analyser);
    oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
    
    const data = new Float32Array(analyser.frequencyBinCount);
    analyser.getFloatFrequencyData(data);
    
    return Array.from(data).join(',');
  } catch (error) {
    return '';
  }
};

const getWebGLFingerprint = async (): Promise<string> => {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
  if (!gl) return '';

  try {
    const info = [
      gl.getParameter(gl.VENDOR),
      gl.getParameter(gl.RENDERER),
      gl.getParameter(gl.VERSION),
      gl.getParameter(gl.SHADING_LANGUAGE_VERSION)
    ].filter(Boolean).join('|');

    return info;
  } catch (error) {
    console.error('Error getting WebGL fingerprint:', error);
    return '';
  }
};

const getFontFingerprint = async (): Promise<string> => {
  const baseFonts = ['monospace', 'sans-serif', 'serif'];
  const testString = 'mmmmmmmmmmlli';
  const testSize = '72px';
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const getFontWidth = (font: string) => {
    ctx.font = testSize + ' ' + font;
    return ctx.measureText(testString).width;
  };

  const baseWidths = baseFonts.map(getFontWidth);
  const fonts = [
    'Arial', 'Verdana', 'Times New Roman', 'Courier New', 
    'Georgia', 'Trebuchet MS', 'Impact'
  ];

  return fonts.map(font => {
    return baseFonts.map((baseFont, index) => {
      ctx.font = testSize + ' ' + font + ', ' + baseFont;
      const width = ctx.measureText(testString).width;
      return width !== baseWidths[index];
    }).join('');
  }).join('|');
};

const getHardwareInfo = async (): Promise<string> => {
  interface ExtendedNavigator extends Navigator {
    deviceMemory?: number;
  }

  const nav = navigator as ExtendedNavigator;

  const info = [
    nav.deviceMemory || 'unknown',
    navigator.hardwareConcurrency,
    'ontouchstart' in window,
    navigator.maxTouchPoints,
    screen.colorDepth,
    screen.pixelDepth,
    window.devicePixelRatio
  ].join('|');

  return info;
};

const getVisitorInfo = async () => {
  interface ExtendedNavigator extends Navigator {
    deviceMemory?: number;
    connection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
      saveData?: boolean;
    };
    getBattery?: () => Promise<any>;
  }

  const nav = navigator as ExtendedNavigator;
  const info: any = {};

  info.visitor_ip = await getVisitorIdentifier();

  const userAgent = navigator.userAgent;
  info.user_agent = userAgent;
  info.is_mobile = /Mobile|Android|iPhone|iPad|iPod|webOS|IEMobile|Opera Mini/i.test(userAgent);
  info.device_type = info.is_mobile ? 'Mobile' : 'Desktop';
  info.browser = getBrowser(userAgent);
  info.operating_system = getOS(userAgent);
  
  info.screen_resolution = `${window.screen.width}x${window.screen.height}`;
  info.viewport_size = `${window.innerWidth}x${window.innerHeight}`;
  info.pixel_ratio = window.devicePixelRatio;
  info.color_depth = window.screen.colorDepth;
  
  info.language = navigator.language;
  info.languages = navigator.languages?.join(',');
  info.do_not_track = navigator.doNotTrack;
  info.cookies_enabled = navigator.cookieEnabled;
  info.local_storage = !!window.localStorage;
  info.session_storage = !!window.sessionStorage;
  
  info.referrer = document.referrer;
  info.referrer_domain = document.referrer ? new URL(document.referrer).hostname : null;
  
  info.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  info.timezone_offset = new Date().getTimezoneOffset();

  if (nav.connection) {
    info.connection_type = nav.connection.effectiveType;
    info.connection_speed = nav.connection.downlink;
    info.connection_rtt = nav.connection.rtt;
    info.connection_saveData = nav.connection.saveData;
  }

  if (nav.getBattery) {
    try {
      const battery = await nav.getBattery();
      info.battery = {
        level: battery.level,
        charging: battery.charging,
        chargingTime: battery.chargingTime,
        dischargingTime: battery.dischargingTime
      };
    } catch (error) {
      console.error('Battery status error:', error);
    }
  }

  info.device_memory = nav.deviceMemory;
  info.hardware_concurrency = navigator.hardwareConcurrency;
  info.max_touch_points = navigator.maxTouchPoints;
  info.pdf_viewer_enabled = navigator.pdfViewerEnabled;

  try {
    const webglCanvas = document.createElement('canvas');
    const gl = webglCanvas.getContext('webgl');
    if (gl) {
      info.gpu = {
        vendor: gl.getParameter(gl.VENDOR),
        renderer: gl.getParameter(gl.RENDERER),
        webgl_version: gl.getParameter(gl.VERSION)
      };
    }
  } catch (error) {
    console.error('WebGL detection error:', error);
  }

  try {
    const locationServices = [
      'https://ipapi.co/json/',
      'https://ip-api.com/json/',
      'https://ipwhois.app/json/'
    ];

    for (const service of locationServices) {
      try {
        const response = await fetch(service);
        const data = await response.json();
        if (data.country || data.country_name) {
          info.country = data.country_name || data.country;
          info.city = data.city;
          info.region = data.region || data.region_name;
          info.latitude = data.latitude || data.lat;
          info.longitude = data.longitude || data.lon;
          info.isp = data.isp || data.org;
          info.asn = data.as || data.asn;
          info.postal = data.postal || data.zip;
          break;
        }
      } catch (error) {
        continue;
      }
    }
  } catch (error) {
    console.error('Error fetching location:', error);
  }

  if (window.performance) {
    const perf = window.performance;
    const timing = perf.timing;
    info.performance = {
      dns: timing.domainLookupEnd - timing.domainLookupStart,
      tcp: timing.connectEnd - timing.connectStart,
      ttfb: timing.responseStart - timing.navigationStart,
      dom_load: timing.domContentLoadedEventEnd - timing.navigationStart,
      full_load: timing.loadEventEnd - timing.navigationStart,
      first_paint: performance.getEntriesByType('paint')
        .find(entry => entry.name === 'first-paint')?.startTime,
      first_contentful_paint: performance.getEntriesByType('paint')
        .find(entry => entry.name === 'first-contentful-paint')?.startTime
    };

    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navEntry) {
      info.performance.navigation = {
        type: navEntry.type,
        redirect_count: navEntry.redirectCount,
        redirect_time: navEntry.redirectEnd - navEntry.redirectStart,
        worker_time: navEntry.workerStart > 0 ? navEntry.responseEnd - navEntry.workerStart : 0,
        request_time: navEntry.responseEnd - navEntry.requestStart,
        response_time: navEntry.responseEnd - navEntry.responseStart,
        cache_time: navEntry.domainLookupStart - navEntry.fetchStart
      };
    }
  }

  return info;
};

const getBrowser = (userAgent: string) => {
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  if (userAgent.includes('Opera')) return 'Opera';
  return 'Other';
};

const getOS = (userAgent: string) => {
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Mac OS')) return 'MacOS';
  if (userAgent.includes('Linux')) return 'Linux';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iOS')) return 'iOS';
  return 'Other';
};

const MySpace = ({ isPublic = false }: MySpaceProps) => {
  const navigate = useNavigate();
  const { username } = useParams();
  
  const { loading, error, profile: rawProfile, listings } = useProfileData({ 
    username: isPublic ? username : undefined, 
    isPublic 
  });

  const profile: Profile | null = rawProfile ? {
    ...rawProfile,
    faqs: rawProfile.faqs as Json,
    show_scheduling: true,
    call_sms_widget: true,
    email: rawProfile.email
  } : null;

  const handleListingClick = (listingId: string) => {
    if (visitId) {
      supabase
        .from('profile_interactions')
        .insert({
          profile_id: profile?.id,
          profile_visit_id: visitId,
          interaction_type: 'listing_click',
          details: {
            listing_id: listingId,
            timestamp: new Date().toISOString()
          }
        })
        .then(({ error }) => {
          if (error) console.error('Error tracking listing click:', error);
        });
    }
    navigate(`/listing/${listingId}`);
  };

  const handleCreateSpace = () => {
    navigate("/auth?mode=signup");
  };

  const [pageLoadStart] = useState(Date.now());
  const [visitId, setVisitId] = useState<string | null>(null);
  const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now());
  const [interactionCount, setInteractionCount] = useState<number>(0);
  const [isTracking, setIsTracking] = useState(false);

  useEffect(() => {
    let isSubscribed = true;
    let trackingInterval: NodeJS.Timeout;

    const initializeTracking = async () => {
      if (!isPublic || !profile?.id || isTracking) return;

      try {
        const visitorInfo = await getVisitorInfo();
        const pageLoadTime = Date.now() - pageLoadStart;
        
        console.log('Starting visit tracking:', { profileId: profile.id, visitorInfo });

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);

        const { data: visitData, error: insertError } = await supabase
          .from('profile_visits')
          .insert({
            profile_id: profile.id,
            visitor_ip: visitorInfo.visitor_ip,
            page_load_time: pageLoadTime,
            device_type: visitorInfo.device_type,
            browser: visitorInfo.browser,
            operating_system: visitorInfo.operating_system,
            screen_resolution: visitorInfo.screen_resolution,
            country: visitorInfo.country,
            city: visitorInfo.city,
            is_mobile: visitorInfo.is_mobile,
            language: visitorInfo.language,
            referrer: visitorInfo.referrer,
            user_agent: visitorInfo.user_agent
          })
          .select()
          .single();

        if (insertError) {
          console.error('Error creating visit record:', insertError);
          return;
        }

        if (visitData && isSubscribed) {
          console.log('Visit record created:', visitData);
          setVisitId(visitData.id);
          setIsTracking(true);

          trackingInterval = setInterval(async () => {
            const timeOnPage = (Date.now() - pageLoadStart) / 1000;
            const timeSinceLastActivity = (Date.now() - lastActivityTime) / 1000;

            const { error: updateError } = await supabase
              .from('profile_visits')
              .update({
                time_on_page: timeOnPage,
                last_activity: timeSinceLastActivity,
                interaction_count: interactionCount
              })
              .eq('id', visitData.id);

            if (updateError) {
              console.error('Error updating visit metrics:', updateError);
            }
          }, 10000);
        }
      } catch (error) {
        console.error('Failed to initialize tracking:', error);
      }
    };

    initializeTracking();

    return () => {
      isSubscribed = false;
      if (trackingInterval) {
        clearInterval(trackingInterval);
      }
      if (visitId) {
        const timeOnPage = (Date.now() - pageLoadStart) / 1000;
        supabase
          .from('profile_visits')
          .update({
            time_on_page: timeOnPage,
            exit_page: true
          })
          .eq('id', visitId)
          .then(({ error }) => {
            if (error) console.error('Error updating final visit metrics:', error);
          });
      }
    };
  }, [isPublic, profile?.id, pageLoadStart, lastActivityTime, interactionCount]);

  useEffect(() => {
    if (!visitId || !isPublic) return;

    let maxScroll = 0;
    let lastScrollUpdate = Date.now();
    let scrollUpdateTimeout: NodeJS.Timeout;

    const throttledScrollHandler = () => {
      if (Date.now() - lastScrollUpdate < 100) return;
      
      const scrollPercent = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100;
      maxScroll = Math.max(maxScroll, scrollPercent);
      lastScrollUpdate = Date.now();
      setLastActivityTime(Date.now());

      clearTimeout(scrollUpdateTimeout);
      scrollUpdateTimeout = setTimeout(() => {
        supabase
          .from('profile_visits')
          .update({
            scroll_depth: maxScroll,
            interaction_details: {
              last_scroll_position: window.scrollY,
              viewport_height: window.innerHeight,
              document_height: document.documentElement.scrollHeight,
              visible_area: (window.innerHeight / document.documentElement.scrollHeight) * 100
            }
          })
          .eq('id', visitId)
          .then(({ error }) => {
            if (error) console.error('Error updating scroll metrics:', error);
          });
      }, 500);
    };

    const handleInteraction = (type: string, details: any = {}) => {
      setLastActivityTime(Date.now());
      setInteractionCount(prev => prev + 1);

      supabase
        .from('profile_interactions')
        .insert({
          profile_id: profile?.id,
          profile_visit_id: visitId,
          interaction_type: type,
          details: {
            ...details,
            timestamp: new Date().toISOString(),
            url: window.location.href
          }
        })
        .then(({ error }) => {
          if (error) console.error('Error tracking interaction:', error);
        });
    };

    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    window.addEventListener('mousemove', () => handleInteraction('mousemove'));
    window.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      handleInteraction('click', {
        target: target.tagName,
        class: target.className,
        id: target.id
      });
    });

    document.addEventListener('visibilitychange', () => {
      handleInteraction('visibility_change', {
        state: document.visibilityState
      });
    });

    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
      window.removeEventListener('mousemove', () => handleInteraction('mousemove'));
      window.removeEventListener('click', () => handleInteraction('click'));
    };
  }, [visitId, profile?.id, isPublic]);

  const metaTitle = profile ? `${profile.full_name} - Professional Space | GetListSpace` : 'GetListSpace - Your Instant Digital Space';
  const metaDescription = profile 
    ? `View ${profile.full_name}'s professional showcase${profile.position && profile.company ? ` - ${profile.position} at ${profile.company}` : ''}. Connect and explore their work on GetListSpace.`
    : 'Create and share your digital space instantly with GetListSpace. Your professional showcase, portfolio, and personal brand - all in one place.';
  const metaImage = "https://getlistspace.com/lovable-uploads/19cb2d94-b4a7-4c95-8cd7-5ee881f66770.png";
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) return <LoadingState />;
  if (error || !profile) return <ErrorState error={error} isPublic={isPublic} />;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={metaImage} />
        {profile?.position && <meta property="og:profile:role" content={profile.position} />}
        {profile?.company && <meta property="og:profile:organization" content={profile.company} />}
      </Helmet>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gradient-to-b from-blue-50/50 via-white to-blue-50/30"
      >
        <ProfileNav isPublic={isPublic} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 flex-1">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-[40%] -right-[20%] w-[80%] h-[80%] rounded-full bg-gradient-to-br from-purple-200/20 to-blue-200/20 blur-3xl" />
            <div className="absolute -bottom-[40%] -left-[20%] w-[80%] h-[80%] rounded-full bg-gradient-to-tr from-blue-200/20 to-purple-200/20 blur-3xl" />
          </div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="relative space-y-6"
          >
            <div className="backdrop-blur-xl bg-white/80 rounded-3xl shadow-xl shadow-blue-200/50 border border-blue-100/50 overflow-hidden">
              <ProfileHeader profile={profile} isPublic={isPublic} />
            </div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="backdrop-blur-xl bg-white/80 rounded-3xl shadow-xl shadow-blue-200/50 border border-blue-100/50 overflow-hidden"
            >
              <ListingsSection 
                listings={listings}
                isPublic={isPublic}
                onListingClick={handleListingClick}
              />
            </motion.div>
          </motion.div>
        </div>

        <motion.footer
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="py-6 text-center text-sm text-gray-500 border-t border-blue-100/50 bg-white/50 backdrop-blur-sm"
        >
          <div className="flex flex-col items-center gap-4">
            <p className="font-medium">GetListSpace made by Raniel Saplan © 2025</p>
            {isPublic && (
              <Button
                onClick={handleCreateSpace}
                variant="default"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300"
              >
                Create Your Space Now
              </Button>
            )}
          </div>
        </motion.footer>

        {isPublic && <ContactWidget profile={profile} />}
      </motion.div>
    </>
  );
};

export default MySpace;
